import { create } from "zustand";
import { CheckCircle2, Info, X, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/cn";

export type ToastVariant = "default" | "success" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Milliseconds. `0` keeps the toast until dismissed. */
  duration?: number;
  action?: ToastAction;
}

interface ToastItem extends ToastOptions {
  id: number;
}

interface ToastState {
  items: ToastItem[];
  push: (item: ToastItem) => void;
  dismiss: (id: number) => void;
}

const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (item) => set((state) => ({ items: [...state.items, item] })),
  dismiss: (id) =>
    set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
}));

const timers = new Map<number, number>();
let nextId = 1;

/** Show a toast. Returns the id so it can be dismissed programmatically. */
export function toast(options: ToastOptions): number {
  const id = nextId++;
  const duration = options.duration ?? (options.action ? 8000 : 4000);
  useToastStore.getState().push({ ...options, id, duration });

  if (duration > 0) {
    timers.set(
      id,
      window.setTimeout(() => dismissToast(id), duration),
    );
  }
  return id;
}

export function dismissToast(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timers.delete(id);
  }
  useToastStore.getState().dismiss(id);
}

/** Convenience wrappers, mostly so call sites read nicely. */
export const notify = {
  success: (title: string, description?: string) =>
    toast({ title, description, variant: "success" }),
  error: (title: string, description?: string) =>
    toast({ title, description, variant: "error", duration: 6000 }),
};

export function Toaster() {
  const items = useToastStore((s) => s.items);

  return (
    <div
      className="pointer-events-none fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:items-end"
      aria-live="polite"
      role="status"
    >
      {items.map((item) => {
        const Icon =
          item.variant === "success"
            ? CheckCircle2
            : item.variant === "error"
              ? AlertTriangle
              : Info;

        return (
          <div
            key={item.id}
            className={cn(
              "pointer-events-auto flex w-full max-w-sm animate-scale-in items-start gap-2.5 rounded-xl border bg-paper px-3.5 py-3 shadow-pop",
              item.variant === "error" ? "border-danger/40" : "border-line",
            )}
          >
            <Icon
              size={16}
              className={cn(
                "mt-0.5 shrink-0",
                item.variant === "success"
                  ? "text-success"
                  : item.variant === "error"
                    ? "text-danger"
                    : "text-ink-faint",
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium leading-5">{item.title}</p>
              {item.description && (
                <p className="mt-0.5 text-[12px] leading-5 text-ink-faint">
                  {item.description}
                </p>
              )}
            </div>
            {item.action && (
              <button
                type="button"
                onClick={() => {
                  item.action?.onClick();
                  dismissToast(item.id);
                }}
                className="shrink-0 rounded-md px-1.5 py-0.5 text-[12px] font-semibold text-accent transition hover:bg-accentSoft"
              >
                {item.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismissToast(item.id)}
              className="-mr-1 shrink-0 rounded p-1 text-ink-faint transition hover:bg-shell hover:text-ink"
              aria-label="Đóng thông báo"
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
